import React, { useState, useEffect } from 'react';
import {
    Box, Button, Card, CardContent, Typography, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Chip, Alert, CircularProgress, Paper, Switch,
    FormControlLabel, Grid, Tooltip
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Router as RouterIcon,
    Speed as SpeedIcon
} from '@mui/icons-material';
import {
    getMikrotiks,
    createMikrotik,
    updateMikrotik,
    deleteMikrotik,
    testMikrotikConnection
} from '../api/mikrotik.api';

const MikroTiks = () => {
    const [mikrotiks, setMikrotiks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingMikrotik, setEditingMikrotik] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        host: '',
        port: 8728,
        username: '',
        password: '',
        ssl: false,
        enabled: true
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [testing, setTesting] = useState(null);

    useEffect(() => {
        loadMikrotiks();
    }, []);

    const loadMikrotiks = async () => {
        setLoading(true);
        try {
            const response = await getMikrotiks({ limit: 100 });
            setMikrotiks(response.data);
        } catch (error) {
            setError('Error cargando MikroTiks');
        }
        setLoading(false);
    };

    const handleOpenDialog = (mikrotik = null) => {
        if (mikrotik) {
            setEditingMikrotik(mikrotik);
            setFormData({
                nombre: mikrotik.nombre,
                host: mikrotik.host,
                port: mikrotik.port,
                username: mikrotik.username,
                password: '',
                ssl: mikrotik.ssl,
                enabled: mikrotik.enabled
            });
        } else {
            setEditingMikrotik(null);
            setFormData({
                nombre: '',
                host: '',
                port: 8728,
                username: '',
                password: '',
                ssl: false,
                enabled: true
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingMikrotik(null);
        setFormData({
            nombre: '',
            host: '',
            port: 8728,
            username: '',
            password: '',
            ssl: false,
            enabled: true
        });
    };

    const handleSubmit = async () => {
        try {
            if (editingMikrotik) {
                const updateData = { ...formData };
                if (!updateData.password) delete updateData.password;
                await updateMikrotik(editingMikrotik.id, updateData);
                setSuccess('MikroTik actualizado exitosamente');
            } else {
                await createMikrotik(formData);
                setSuccess('MikroTik creado exitosamente');
            }
            handleCloseDialog();
            loadMikrotiks();
        } catch (error) {
            setError(error.response?.data?.detail || 'Error al guardar MikroTik');
        }
    };

    const handleDelete = async (id, nombre) => {
        if (!window.confirm(`¿Eliminar MikroTik "${nombre}"?`)) return;
        try {
            await deleteMikrotik(id);
            setSuccess('MikroTik eliminado exitosamente');
            loadMikrotiks();
        } catch (error) {
            setError('Error al eliminar MikroTik');
        }
    };

    const handleTestConnection = async (id) => {
        setTesting(id);
        try {
            const response = await testMikrotikConnection(id);
            if (response.data.success) {
                alert(`✅ Conexión exitosa\n\nInformación:\n${JSON.stringify(response.data.info, null, 2)}`);
            } else {
                alert(`❌ Error de conexión: ${response.data.message}`);
            }
        } catch (error) {
            alert('Error al probar conexión');
        }
        setTesting(null);
    };

    return (
        <Box sx={{ p: 3 }}>
            {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5">MikroTiks</Typography>
                <Box>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadMikrotiks}
                        sx={{ mr: 2 }}
                    >
                        Refrescar
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        Nuevo MikroTik
                    </Button>
                </Box>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Nombre</TableCell>
                                <TableCell>Host</TableCell>
                                <TableCell>Puerto</TableCell>
                                <TableCell>Usuario</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell align="center">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {mikrotiks.map((mikrotik) => (
                                <TableRow key={mikrotik.id}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <RouterIcon sx={{ mr: 1, color: 'primary.main' }} />
                                            <Typography variant="body2" fontWeight="bold">
                                                {mikrotik.nombre}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{mikrotik.host}</TableCell>
                                    <TableCell>{mikrotik.port}</TableCell>
                                    <TableCell>{mikrotik.username}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={mikrotik.enabled ? 'Habilitado' : 'Deshabilitado'}
                                            color={mikrotik.enabled ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Probar conexión">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleTestConnection(mikrotik.id)}
                                                disabled={testing === mikrotik.id}
                                            >
                                                {testing === mikrotik.id ? <CircularProgress size={20} /> : <SpeedIcon />}
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Editar">
                                            <IconButton size="small" onClick={() => handleOpenDialog(mikrotik)}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Eliminar">
                                            <IconButton size="small" color="error" onClick={() => handleDelete(mikrotik.id, mikrotik.nombre)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {mikrotiks.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <Typography color="textSecondary">No hay MikroTiks registrados</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Dialog para crear/editar MikroTik */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingMikrotik ? 'Editar MikroTik' : 'Nuevo MikroTik'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                label="Nombre"
                                fullWidth
                                value={formData.nombre}
                                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={8}>
                            <TextField
                                label="Host / IP"
                                fullWidth
                                value={formData.host}
                                onChange={(e) => setFormData({ ...formData, host: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                label="Puerto"
                                type="number"
                                fullWidth
                                value={formData.port}
                                onChange={(e) => setFormData({ ...formData, port: parseInt(e.target.value) })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Usuario"
                                fullWidth
                                value={formData.username}
                                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                label="Contraseña"
                                type="password"
                                fullWidth
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                placeholder={editingMikrotik ? "Dejar en blanco para no cambiar" : ""}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.ssl}
                                        onChange={(e) => setFormData({ ...formData, ssl: e.target.checked })}
                                    />
                                }
                                label="Usar SSL"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.enabled}
                                        onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                                    />
                                }
                                label="Habilitado"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancelar</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        {editingMikrotik ? 'Actualizar' : 'Crear'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MikroTiks;
